import { cloneElement, forwardRef, isValidElement } from 'react';
import Button from '../Button';
import TextField from '../TextField';
import { getQTipData } from '../Tooltip/qTip.utilities';
import type { InputGroupProps } from './InputGroup.types';

const baseClasses = 'tw:flex tw:outline-none tw:rounded-md tw:w-full tw:relative tw:flex-wrap';

const errorClasses = 'tw:border-sq-danger';

const borderColorClasses = [
  'tw:border-sq-disabled-gray',
  'tw:dark:border-sq-dark-disabled-gray',
  'tw:dark:focus:border-sq-theme-darker',
  'tw:dark:active:border-sq-theme-darker',
  'tw:focus:border-sq-theme-dark',
  'tw:active:border-sq-theme-dark',
].join(' ');

const fieldClasses = 'tw:leading-normal tw:outline-none tw:py-1 tw:px-3 tw:p-1 tw:border-solid tw:border';

const darkTheme = 'tw:dark:bg-sq-dark-background tw:dark:text-sq-dark-text tw:disabled:dark:text-sq-disabled-gray';
const lightTheme = 'tw:text-sq-text-color tw:disabled:text-sq-darkish-gray';

/**
 * InputGroup.
 */
export const InputGroup = forwardRef<HTMLInputElement, InputGroupProps>((props, ref) => {
  const {
    readonly = false,
    onChange,
    onKeyUp,
    onFocus,
    onBlur,
    onKeyDown,
    name,
    value,
    placeholder,
    append = [],
    extraClassNames = '',
    id,
    testId,
    showError,
    required,
    autoComplete,
    autoFocus,
    disabled,
    type,
    step,
    min,
    max,
    field,
    maxLength,
    minLength,
    inputGroup,
    ...tooltipProps
  } = props;
  const tooltipData = getQTipData(tooltipProps);

  const appliedClasses = `${baseClasses} ${extraClassNames}`;

  const elementsToAppend = append.filter(Boolean);
  const effectiveInputGroup = inputGroup ?? (elementsToAppend.length > 0 ? 'left' : undefined);

  const fieldBorderRadius =
    effectiveInputGroup === 'left'
      ? 'tw:rounded-l-md tw:rounded-r-none'
      : effectiveInputGroup === 'right'
        ? 'tw:rounded-r-md tw:rounded-l-none'
        : 'tw:rounded-md';

  const fieldAppliedClasses = `${fieldClasses} ${extraClassNames} ${lightTheme} ${darkTheme} ${fieldBorderRadius} ${
    showError ? errorClasses : borderColorClasses
  } `;

  // Only pass inputGroup to React components (not DOM elements like div, span, etc.)
  const isReactComponent = isValidElement(field) && typeof field.type !== 'string';
  const fieldWithInputGroup = isReactComponent
    ? cloneElement(field as React.ReactElement<{ inputGroup?: 'left' | 'right' }>, {
        inputGroup: effectiveInputGroup,
      })
    : field;

  return (
    <div id={`input-group-${id}`} className={appliedClasses}>
      {field ? (
        <div
          data-testid={testId}
          className={`tw:flex tw:flex-1 tw:cursor-pointer tw:active:z-50 ${fieldAppliedClasses}`}>
          {fieldWithInputGroup}
        </div>
      ) : (
        <TextField
          testId={testId}
          readonly={readonly}
          onChange={onChange}
          onKeyUp={onKeyUp}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          name={name}
          ref={ref}
          showError={showError}
          value={value}
          required={required}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          id={id}
          type={type}
          disabled={disabled}
          step={step}
          min={min}
          max={max}
          maxLength={maxLength}
          minLength={minLength}
          inputGroup={effectiveInputGroup}
          extraClassNames={`tw:flex tw:flex-1 tw:w-full tw:focus:z-30 ${extraClassNames}`}
          {...tooltipData}
        />
      )}

      {elementsToAppend.map((item, index) => {
        return item?.variant === 'button' ? (
          <Button
            key={index}
            extraClassNames={`$
                index ? 'tw:-ml-px' : 'tw:ml-0'
              } tw:focus:z-40 tw:focus:border tw:rounded-none tw:last:rounded-r-md`}
            {...item.buttonProps}
          />
        ) : (
          <div
            key={index}
            className={`${borderColorClasses} ${
              index ? 'tw:-ml-px' : 'tw:ml-0'
            } tw:flex tw:items-center tw:justify-center tw:rounded-none tw:last:rounded-r-md tw:active:z-30 tw:active:border tw:border`}>
            {item?.element}
          </div>
        );
      })}
    </div>
  );
});
InputGroup.displayName = 'InputGroup';
